import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Settings as SettingsIcon, Image as ImageIcon, Type, Upload, MapPin, Navigation, Fingerprint, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Settings.css';

const DEVICE_TYPES = [
  { value: 'zkteco',          label: 'ZKTeco' },
  { value: 'digital_persona', label: 'DigitalPersona (U.are.U)' },
  { value: 'suprema',         label: 'Suprema BioStar' },
  { value: 'generic_usb',     label: 'Generic USB Reader' },
  { value: 'webauthn',        label: 'WebAuthn / Browser (FIDO2)' },
];

const CONNECTION_MODES = [
  { value: 'usb_local',         label: 'USB Local (same machine)' },
  { value: 'network_tcp',       label: 'Network TCP/IP' },
  { value: 'websocket_bridge',  label: 'WebSocket Bridge (local middleware)' },
  { value: 'browser_webauthn',  label: 'Browser WebAuthn API' },
];

const BiometricDeviceSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/biometric/device-config');
      setConfig(res?.data ?? res);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setMessage({ type: '', text: '' });
    setTestResult(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.put('/biometric/device-config', config);
      setConfig(res?.data ?? res);
      setMessage({ type: 'success', text: 'Biometric device configuration saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save configuration.' });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/biometric/device-config/test');
      setTestResult(res?.data ?? res);
    } catch (err) {
      setTestResult({ success: false, message: err?.response?.data?.message || 'Test failed.' });
    }
    setTesting(false);
  };


  if (loading) return null;

  return (
    <div className="settings-section biometric-section">
      <h3 className="section-title">
        <Fingerprint size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
        Biometric Device
      </h3>

      {message.text && (
        <div className={`settings-msg ${message.type}`}>{message.text}</div>
      )}

      <div className="settings-row">
        <label>Device Type</label>
        <select
          value={config?.deviceType ?? 'generic_usb'}
          onChange={(e) => handleChange('deviceType', e.target.value)}
        >
          {DEVICE_TYPES.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>

      <div className="settings-row">
        <label>Connection Mode</label>
        <select
          value={config?.connectionMode ?? 'network_tcp'}
          onChange={(e) => handleChange('connectionMode', e.target.value)}
        >
          {CONNECTION_MODES.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      <div className="settings-row">
        <label>Device IP Address</label>
        <input
          type="text"
          value={config?.deviceIp ?? ''}
          onChange={(e) => handleChange('deviceIp', e.target.value)}
          placeholder="e.g. 192.168.1.100"
        />
      </div>

      <div className="settings-row">
        <label>Device Port</label>
        <input
          type="number"
          value={config?.devicePort ?? 4370}
          onChange={(e) => handleChange('devicePort', parseInt(e.target.value, 10))}
          placeholder="4370"
          min={1}
          max={65535}
        />
      </div>

      <div className="settings-row">
        <label>SDK Bridge URL (optional)</label>
        <input
          type="text"
          value={config?.sdkBridgeUrl ?? ''}
          onChange={(e) => handleChange('sdkBridgeUrl', e.target.value)}
          placeholder="ws://localhost:8765"
        />
      </div>

      <div className="settings-row">
        <label>Minimum Quality Score</label>
        <input
          type="number"
          value={config?.minQualityScore ?? 60}
          onChange={(e) => handleChange('minQualityScore', parseInt(e.target.value, 10))}
          min={0}
          max={100}
          style={{ width: 80 }}
        />
        <small style={{ marginLeft: 8, color: '#6b7280' }}>out of 100</small>
      </div>

      {testResult && (
        <div className={`biometric-test-result ${testResult.success ? 'success' : 'error'}`}>
          {testResult.success ? <Wifi size={14} /> : <WifiOff size={14} />}
          <span>{testResult.message}</span>
        </div>
      )}

      <div className="biometric-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleTest}
          disabled={testing}
        >
          {testing ? 'Testing…' : 'Test Connection'}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

const Settings = () => {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState({
    brandName: '',
    brandSubtitle: '',
    logoUrl: '',
    officeLocation: { lat: 35.819871, lng: 10.592260 }, // Default: Tunis, Tunisia
    allowedRadius: 100, // Default: 100 meters
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

  const optimizeImageDataUrl = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        const MAX_DB_SAFE_LENGTH = 55000;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to optimize image'));
          return;
        }

        const maxSize = 220;
        const baseRatio = Math.min(maxSize / img.width, maxSize / img.height, 1);
        let width = Math.max(1, Math.floor(img.width * baseRatio));
        let height = Math.max(1, Math.floor(img.height * baseRatio));
        let quality = 0.72;
        let optimized = '';

        for (let i = 0; i < 8; i += 1) {
          canvas.width = width;
          canvas.height = height;
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          optimized = canvas.toDataURL('image/jpeg', quality);

          if (optimized.length <= MAX_DB_SAFE_LENGTH) {
            resolve(optimized);
            return;
          }

          quality = Math.max(0.35, quality - 0.08);
          width = Math.max(64, Math.floor(width * 0.85));
          height = Math.max(64, Math.floor(height * 0.85));
        }

        if (optimized.length > MAX_DB_SAFE_LENGTH) {
          reject(new Error('Image is still too large after optimization. Please use a smaller file.'));
          return;
        }

        resolve(optimized);
      };
      img.onerror = () => reject(new Error('Invalid image file'));
      img.src = dataUrl;
    });

  const ensureDefaultCompany = async () => {
    try {
      await api.post('/companies', {
        name: 'PURA Solutions',
        isActive: true,
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!isAdmin()) return;
      try {
        const branding = await api.get('/companies/1/branding');
        const savedLocation = localStorage.getItem('officeLocation');
        const savedRadius = localStorage.getItem('allowedRadius');
        
        setForm({
          brandName: branding?.customizations?.portalBrandName || branding?.companyName || 'PURA Solutions',
          brandSubtitle: branding?.customizations?.portalSubtitle || 'Management Portal',
          logoUrl: branding?.customizations?.portalLogoDataUrl || branding?.logoUrl || '',
          officeLocation: savedLocation ? JSON.parse(savedLocation) : (branding?.customizations?.officeLocation || { lat: 35.819871, lng: 10.592260 }),
          allowedRadius: savedRadius ? parseInt(savedRadius) : (branding?.customizations?.allowedRadius || 100),
        });
      } catch (error) {
        const created = await ensureDefaultCompany();
        if (created) {
          try {
            const branding = await api.get('/companies/1/branding');
            const savedLocation = localStorage.getItem('officeLocation');
            const savedRadius = localStorage.getItem('allowedRadius');
            
            setForm({
              brandName: branding?.customizations?.portalBrandName || branding?.companyName || 'PURA Solutions',
              brandSubtitle: branding?.customizations?.portalSubtitle || 'Management Portal',
              logoUrl: branding?.customizations?.portalLogoDataUrl || branding?.logoUrl || '',
              officeLocation: savedLocation ? JSON.parse(savedLocation) : (branding?.customizations?.officeLocation || { lat: 35.819871, lng: 10.592260 }),
              allowedRadius: savedRadius ? parseInt(savedRadius) : (branding?.customizations?.allowedRadius || 100),
            });
            setMessage({ type: 'info', text: 'Default company initialized.' });
            return;
          } catch (retryError) {
            setMessage({ type: 'error', text: 'Failed to load global settings.' });
            return;
          }
        }
        setMessage({ type: 'error', text: 'Failed to load global settings.' });
      }
    };
    load();
  }, [isAdmin]);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ type: '', text: '' }), 2500);
    return () => clearTimeout(timer);
  }, [message]);

  // OpenStreetMap with Leaflet functions (no API key required)
  const initializeMap = () => {
    if (!window.L || !mapRef.current) return;

    // Remove existing map if it exists
    if (map) {
      map.remove();
    }

    const mapOptions = {
      center: [form.officeLocation.lat, form.officeLocation.lng],
      zoom: 15,
      zoomControl: true,
    };

    const newMap = window.L.map(mapRef.current, mapOptions);
    
    // Add OpenStreetMap tiles (free, no API key required)
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(newMap);

    // Add custom marker for office location
    const customIcon = window.L.divIcon({
      html: '<div style="background: #3b82f6; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"><MapPin size={16} /></div>',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15],
      className: 'custom-marker'
    });

    const newMarker = window.L.marker([form.officeLocation.lat, form.officeLocation.lng], {
      icon: customIcon,
      draggable: true
    }).addTo(newMap);

    newMarker.bindPopup('Office Location - Drag to move').openPopup();

    // Handle marker drag
    newMarker.on('dragend', (event) => {
      const newPosition = event.target.getLatLng();
      setForm(prev => ({ 
        ...prev, 
        officeLocation: { lat: newPosition.lat, lng: newPosition.lng } 
      }));
    });

    // Handle map click
    newMap.on('click', (event) => {
      const newPosition = event.latlng;
      setForm(prev => ({ 
        ...prev, 
        officeLocation: { lat: newPosition.lat, lng: newPosition.lng } 
      }));
      newMarker.setLatLng(newPosition);
    });

    // Add circle to show geofence radius
    const radiusCircle = window.L.circle([form.officeLocation.lat, form.officeLocation.lng], {
      color: '#3b82f6',
      fillColor: '#3b82f6',
      fillOpacity: 0.1,
      radius: form.allowedRadius
    }).addTo(newMap);

    setMap(newMap);
    setMarker(newMarker);
    setMapLoaded(true);

    // Store circle reference for updates
    window._radiusCircle = radiusCircle;
  };

  const loadLeafletScript = () => {
    if (window.L) {
      initializeMap();
      return;
    }

    // Load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      // Load Lucide icons for the marker
      const markerScript = document.createElement('script');
      markerScript.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.js';
      markerScript.onload = initializeMap;
      document.head.appendChild(markerScript);
    };
    document.head.appendChild(script);
  };

  useEffect(() => {
    if (isAdmin()) {
      loadLeafletScript();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (mapLoaded && map) {
      map.setView([form.officeLocation.lat, form.officeLocation.lng], 15);
      if (marker) {
        marker.setLatLng([form.officeLocation.lat, form.officeLocation.lng]);
      }
      // Update radius circle
      if (window._radiusCircle) {
        window._radiusCircle.setLatLng([form.officeLocation.lat, form.officeLocation.lng]);
        window._radiusCircle.setRadius(form.allowedRadius);
      }
    }
  }, [form.officeLocation, form.allowedRadius, mapLoaded, map, marker]);

  const onFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file.' });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      const optimized = await optimizeImageDataUrl(dataUrl);
      setForm((prev) => ({ ...prev, logoUrl: optimized }));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to process image.' });
    }
  };

  const onDrop = async (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please drop an image file.' });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      const optimized = await optimizeImageDataUrl(dataUrl);
      setForm((prev) => ({ ...prev, logoUrl: optimized }));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to process image.' });
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    if (!isAdmin()) {
      setMessage({ type: 'error', text: 'Only admins can update global settings.' });
      return;
    }
    setSaving(true);
    try {
      try {
        await api.get('/companies/1/branding');
      } catch (error) {
        await ensureDefaultCompany();
      }

      await api.patch('/companies/1/branding', {
        logoUrl: null,
        customizations: {
          portalBrandName: form.brandName.trim() || 'PURA Solutions',
          portalSubtitle: form.brandSubtitle.trim() || 'Management Portal',
          portalLogoDataUrl: form.logoUrl || null,
          officeLocation: form.officeLocation,
          allowedRadius: form.allowedRadius,
        },
      });

      localStorage.setItem('portalLogoDataUrl', form.logoUrl || '');
      localStorage.setItem('portalBrandName', form.brandName.trim() || 'PURA Solutions');
      localStorage.setItem('portalSubtitle', form.brandSubtitle.trim() || 'Management Portal');
      localStorage.setItem('officeLocation', JSON.stringify(form.officeLocation));
      localStorage.setItem('allowedRadius', form.allowedRadius.toString());
      window.dispatchEvent(new Event('portal-branding-updated'));

      setMessage({ type: 'success', text: 'Global settings saved.' });
      setTimeout(() => window.location.reload(), 250);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const clearLogo = () => {
    setForm((prev) => ({ ...prev, logoUrl: '' }));
    setMessage({ type: 'info', text: 'Logo removed. Save settings to apply globally.' });
  };

  if (!isAdmin()) {
    return <div className="page-loading">Only admins can access portal settings.</div>;
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1><SettingsIcon size={24} /> Settings</h1>
      </div>

      {message.text && <div className={`remote-message ${message.type}`}>{message.text}</div>}

      <div className="settings-card" style={{ maxWidth: 860 }}>
        <h3 className="settings-section-title">Portal Branding</h3>
        <p className="settings-section-subtitle">These values are global and visible to all users.</p>
        <form onSubmit={saveSettings}>
          <div className="form-row">
            <div className="form-group">
              <label><Type size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Brand Name</label>
              <input
                type="text"
                value={form.brandName}
                onChange={(e) => setForm({ ...form, brandName: e.target.value })}
                placeholder="PURA Solutions"
                required
              />
            </div>
            <div className="form-group">
              <label><Type size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Brand Subtitle</label>
              <input
                type="text"
                value={form.brandSubtitle}
                onChange={(e) => setForm({ ...form, brandSubtitle: e.target.value })}
                placeholder="Management Portal"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label><ImageIcon size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Logo</label>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`logo-dropzone ${dragging ? 'dragging' : ''}`}
            >
              <Upload size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Drag & drop logo here or click to select
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onFileSelect}
            />
            {form.logoUrl && (
              <div className="logo-preview-wrap">
                <img src={form.logoUrl} alt="Logo Preview" className="logo-preview" />
              </div>
            )}
          </div>

          {/* Office Location Section */}
          <div className="location-section">
            <h4 className="settings-subsection-title">
              <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Office Location Settings
            </h4>
            <p className="settings-section-subtitle">Set the office location for geolocation-based attendance tracking.</p>
            
            <div className="form-row">
              <div className="form-group">
                <label><Navigation size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Allowed Radius (meters)</label>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  value={form.allowedRadius}
                  onChange={(e) => setForm({ ...form, allowedRadius: parseInt(e.target.value) || 100 })}
                  placeholder="100"
                />
                <small>Maximum distance from office location for clock-in/out</small>
              </div>
              <div className="form-group">
                <label><MapPin size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Current Location</label>
                <div className="location-display">
                  <span className="location-coords">
                    {form.officeLocation.lat.toFixed(6)}, {form.officeLocation.lng.toFixed(6)}
                  </span>
                </div>
                <small>Click on the map to update the office location</small>
              </div>
            </div>

            <div className="map-container">
              <div 
                ref={mapRef} 
                className="openstreetmap"
                style={{ height: '400px', width: '100%', borderRadius: '8px', marginTop: '16px' }}
              />
              {!mapLoaded && (
                <div className="map-loading">
                  <div className="loading-spinner">Loading map...</div>
                  <p>OpenStreetMap is loading. This free map doesn't require an API key.</p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={clearLogo}>Remove Logo</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </form>

        <BiometricDeviceSection />
      </div>
    </div>
  );
};

export default Settings;
